/**
 * ContextualLoader — progressive loading messages with estimated duration.
 *
 * Messages escalate over time: "Analyse en cours…" → "L'IA réfléchit…" → "Ça prend un peu plus de temps…"
 * After 60s with no response, shows a retry button.
 */

import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';

const MESSAGE_DELAYS = [0, 5000, 20000];
const TIMEOUT_MS = 60_000;

interface ContextualLoaderProps {
  /** Whether loading is active — resets messages when it goes from false→true */
  isLoading: boolean;
  /** Called when user clicks retry after timeout */
  onRetry?: () => void;
  /** Short duration estimate shown below spinner. */
  estimatedDuration?: string;
  className?: string;
}

export function ContextualLoader({
  isLoading,
  onRetry,
  estimatedDuration,
  className,
}: ContextualLoaderProps) {
  const { t } = useI18n();
  const MESSAGES = [
    { delay: MESSAGE_DELAYS[0], text: t('loader.analyzing') },
    { delay: MESSAGE_DELAYS[1], text: t('loader.thinking') },
    { delay: MESSAGE_DELAYS[2], text: t('loader.takingLonger') },
  ];
  const [messageIndex, setMessageIndex] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    if (!isLoading) {
      // Reset via timer callback to satisfy react-hooks/set-state-in-effect rule
      timers.push(setTimeout(() => {
        setMessageIndex(0);
        setTimedOut(false);
      }, 0));
      return () => timers.forEach(clearTimeout);
    }

    MESSAGES.forEach((msg, i) => {
      if (i === 0) return;
      timers.push(setTimeout(() => setMessageIndex(i), msg.delay));
    });

    timers.push(setTimeout(() => setTimedOut(true), TIMEOUT_MS));

    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (timedOut) {
    return (
      <div className={cn('flex flex-col items-center gap-3 py-8', className)}>
        <p className="text-sm text-red-300 text-center">{t('loader.failed')}</p>
        {onRetry && (
          <button
            type="button"
            onClick={() => { setTimedOut(false); setMessageIndex(0); onRetry(); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/70 text-sm font-medium transition-colors"
          >
            <RefreshCw className="size-3.5" />
            {t('loader.retry')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center gap-3 py-8', className)}>
      <Loader2 className="size-8 text-emerald-400 animate-spin" />
      <p className={cn(
        'text-sm text-white/50 text-center transition-all duration-500',
        messageIndex > 0 && 'animate-pulse',
      )}>
        {MESSAGES[messageIndex].text}
      </p>
      {(estimatedDuration ?? t('loader.defaultDuration')) && (
        <p className="text-xs text-white/25">{estimatedDuration ?? t('loader.defaultDuration')}</p>
      )}
    </div>
  );
}
